import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { SheetService } from '../../../core/services/sheet';

/**
 * Bottom sheet partagée — design « Nuit Claire » (§4.4).
 *
 * Voile sombre flouté + panneau `#F4F4F8` arrondi à 26 px, poignée 40×4.
 * Le voile est lui-même un bouton étiqueté « Fermer » : c'est le seul geste de
 * fermeture, la maquette ayant écarté la croix d'en-tête (§14.8, « épuré avant
 * complet »). L'étiquette du voile garde la fermeture accessible aux lecteurs
 * d'écran.
 *
 * S'enregistre auprès de `SheetService` tant qu'elle est ouverte, pour que la
 * nav flottante s'efface au lieu de flotter par-dessus le panneau.
 *
 * `aria-modal` nomme la feuille et sert de garde-fou au geste de retour
 * ([appSwipeBack]), qui ne doit pas naviguer par-dessus.
 *
 * Usage :
 *   <app-sheet [open]="show()" title="Cotiser" (closed)="show.set(false)">
 *     ...contenu projeté...
 *   </app-sheet>
 */
@Component({
  selector: 'app-sheet',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (open()) {
    <div
      class="fixed inset-0 z-[60] flex flex-col justify-end"
      role="dialog"
      aria-modal="true"
      [attr.aria-label]="title() || null"
    >
      <button
        type="button"
        aria-label="Fermer"
        (click)="closed.emit()"
        class="absolute inset-0 backdrop-blur-[2px]"
        style="background: rgba(8,9,18,0.55); animation: te-fade 0.25s ease both"
      ></button>
      <div
        class="relative flex flex-col overflow-hidden rounded-t-sheet bg-sheet transition-[margin-bottom] duration-200"
        [class]="full() ? 'h-full max-h-full' : 'max-h-[88%]'"
        style="margin-bottom: var(--keyboard-height, 0px); animation: te-sheet-in 0.36s var(--ease-ios) both"
      >
        @if (!full()) {
        <div class="flex justify-center pt-3.5">
          <div class="h-1 w-10 rounded-full bg-[#D6D6E0]"></div>
        </div>
        } @if (title()) {
        <div class="px-[22px] pt-3.5">
          <p class="text-[16px] font-extrabold text-ink">{{ title() }}</p>
          @if (subtitle()) {
          <p class="mt-1 text-[12px] font-semibold text-muted">{{ subtitle() }}</p>
          }
        </div>
        }
        <div
          class="te-scroll px-[22px] pt-4 pb-[calc(env(safe-area-inset-bottom,0px)+34px)]"
          [class.flex-1]="full()"
        >
          <ng-content />
        </div>
      </div>
    </div>
    }
  `,
})
export class Sheet {
  readonly open = input<boolean>(false);
  readonly title = input<string>('');
  readonly subtitle = input<string>('');
  readonly full = input<boolean>(false);
  readonly closed = output<void>();

  private readonly sheets = inject(SheetService);

  constructor() {
    let registered = false;
    const release = () => {
      if (registered) {
        this.sheets.unregister();
        registered = false;
      }
    };
    effect(() => {
      if (this.open()) {
        if (!registered) {
          this.sheets.register();
          registered = true;
        }
      } else {
        release();
      }
    });
    inject(DestroyRef).onDestroy(release);
  }
}
